import './season-timer.css';
import { Clock } from 'lucide-react';
import { useLeague, useLeagueSeasonTimer } from '../../../hooks';
import { useEffect, useRef, useState } from 'react';

export function SeasonTimer() {
  const [showFixed, setShowFixed] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);
  const { currentSeason, loading } = useLeague();
  const { timeRemaining } = useLeagueSeasonTimer();

  useEffect(() => {
    const handleScroll = () => {
      // Detectar si el componente principal está visible
      if (mainRef.current) {
        const rect = mainRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        setShowFixed(!isVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Verificar estado inicial
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 🔥 LOADING PLACEHOLDER
  if (loading) {
    return (
      <div className="season-timer loading">
        <div className="season-timer-left">
          <div className="skeleton-icon" />
          <div>
            <h2 className="season-timer-title">Temporada</h2>
          </div>
        </div>

        <div className="season-timer-right">
          <div className="skeleton-box" />
          <div className="skeleton-box" />
          <div className="skeleton-box" />
        </div>
      </div>
    );
  }

  if (!currentSeason) return null;

  const isFinished = timeRemaining.finished;

  return (
    <>
      <div
        ref={mainRef}
        className="season-timer"
        style={
          isFinished
            ? undefined
            : {
                borderLeftColor: currentSeason.bgColor,
                background: `linear-gradient(90deg, ${currentSeason.bgColor}30, ${currentSeason.bgColor}10)`,
              }
        }
      >
        <div className="season-timer-left">
          <Clock
            className="season-timer-icon"
            style={{ color: isFinished ? '#F31000' : currentSeason.bgColor }}
          />

          <div>
            <h2 className="season-timer-title">
              {isFinished ? 'Temporada finalizada' : 'Temporada Activa'}
            </h2>

            {!isFinished && (
              <p className="season-timer-subtitle">La temporada finaliza en:</p>
            )}
          </div>
        </div>

        {!isFinished && (
          <div className="season-timer-right">
            {timeRemaining.days > 0 && (
              <>
                <TimeUnit
                  value={timeRemaining.days}
                  label={timeRemaining.days === 1 ? 'Día' : 'Días'}
                />
                <span className="separator">:</span>
              </>
            )}

            <TimeUnit
              value={timeRemaining.hours}
              label={timeRemaining.hours === 1 ? 'Hora' : 'Horas'}
            />
            <span className="separator">:</span>
            <TimeUnit
              value={timeRemaining.minutes}
              label={timeRemaining.minutes === 1 ? 'Minuto' : 'Minutos'}
            />
            <span className="separator">:</span>
            <TimeUnit
              value={timeRemaining.seconds}
              label={timeRemaining.seconds === 1 ? 'Segundo' : 'Segundos'}
            />
          </div>
        )}
      </div>

      <div
        className={`season-timer-fixed ${showFixed ? 'season-timer-fixed-visible' : ''}`}
      >
        <div className="season-timer-fixed-content">
          <Clock className="season-timer-fixed-icon" />
          <div className="season-timer-fixed-time">
            {timeRemaining.days > 0 && (
              <>{String(timeRemaining.days).padStart(2, '0')}:</>
            )}
            {String(timeRemaining.hours).padStart(2, '0')}:
            {String(timeRemaining.minutes).padStart(2, '0')}:
            {String(timeRemaining.seconds).padStart(2, '0')}
          </div>
        </div>
      </div>
    </>
  );
}

function TimeUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="time-unit">
      <div className="time-box">
        <span className="time-value">{String(value).padStart(2, '0')}</span>
      </div>
      <p className="time-label">{label}</p>
    </div>
  );
}
